import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {ICommentModel} from '@src/@interfaces/models/Comment/ICommentModel';
import Comment from './Comment';
import classnames from '@src/helpers/classnames';
import Translate from '@src/components/Translate/Translate';
import MessageReceiver, {
    makeOnDeleteAttachListener,
    makeOnDropAttachListener,
    makeOnSubmitListener
} from '@src/components/Chat/MessageReceiver/MessageReceiver';
import AttachmentsPreviews from '@src/components/Chat/AttachmentsPreviews/AttachmentsPreviews';
import RenderIf from '@src/components/RenderIf/RenderIf';
import LoadingIndicator from '@src/components/LoadingIndicator/LoadingIndicator';
import useIntersectionObserver from '@src/hooks/useIntersectionObserver';
import useUserId from '@src/hooks/useUserId';

export default function CommentsList(props: {
    list?: ICommentModel[],
    isLoading?: boolean,
    isDisabled?: boolean,
    itemComponent?: React.ComponentType<React.ComponentProps<typeof Comment>>,
    leaveMessageFromComponent?: typeof MessageReceiver,
    allowToReply?: boolean,
    allowToLeaveMessage?: boolean,
    allowToMarkAsRead?: boolean,
    isInvalid?: boolean,
    errorMessage?: string | null,
    leaveMessageFormPosition?: 'top' | 'bottom',
    onPostMessage?: (message: string | null, attachments: File[], answerTo: number | string | null) => Promise<boolean>,
    onCommentDeleted?: (comment: ICommentModel) => void,
    size?: React.ComponentProps<typeof MessageReceiver>['size'],
    maxHeight?: number,
    intersectionObserverRoot?: HTMLElement | null,
} & Omit<React.HTMLProps<HTMLDivElement>, 'list' | 'size'>) {
    const {
        list,
        size,
        isLoading,
        isDisabled,
        className: wrapperClassName,
        itemComponent: Item = Comment,
        leaveMessageFromComponent: LeaveMessageForm = MessageReceiver,
        allowToReply,
        allowToLeaveMessage = props.allowToReply,
        allowToMarkAsRead = true,
        leaveMessageFormPosition: position = 'bottom',
        onPostMessage,
        onCommentDeleted,
        isInvalid,
        errorMessage,
        maxHeight,
        intersectionObserverRoot,
        ...wrapperProps
    } = props ?? {};

    const [searchParams] = useSearchParams();
    const listRef = useRef<HTMLDivElement>(null);
    const [scrolledTo, setScrolledTo] = useState<string | null>(null);

    useEffect(() => {
        if (!maxHeight || !searchParams.has('commentId') || searchParams.get('commentId') === scrolledTo || !listRef.current) {
            return;
        }

        const highlightedItem = listRef.current.querySelector('.comment-list__item--highlighted');
        if (highlightedItem) {
            highlightedItem.scrollIntoView();

            setScrolledTo(searchParams.get('commentId'));
        }
    }, [maxHeight, list?.length, scrolledTo, searchParams]);

    const [answerToMessage, setAnswerToMessage] = useState<ICommentModel | null>(null);
    const [attachments, setAttachments] = useState<File[]>([]);
    const [message, setMessage] = useState<string | null>(null);

    const [onDelete, onDrop, onSubmit] = useMemo(() => {
        return [
            makeOnDeleteAttachListener(setAttachments),
            makeOnDropAttachListener(setAttachments),
            makeOnSubmitListener(onPostMessage, attachments, setAttachments),
        ];
    }, [setAttachments, onPostMessage, attachments]);

    const leaveMessageForm = (() => {
        if (!allowToLeaveMessage) {
            return null;
        }

        return <LeaveMessageForm
                size={size}
                defaultValue={message}
                onInput={setMessage}
                onDrop={onDrop}
                placeholder="CommentsList.message"
                isInvalid={isInvalid}
                isDisabled={isDisabled}
                errorMessage={errorMessage}
                filesPreviewSlot={
                    <AttachmentsPreviews
                            isDisabled={isDisabled}
                            attachment={attachments}
                            onDelete={onDelete}
                    />
                }
                onSubmit={onSubmit}
                itIsAnswerToMessage={answerToMessage}
                onResetAnswerTo={() => void setAnswerToMessage(null)}
        />;
    })();

    const currentUserId = useUserId();
    const observer = useIntersectionObserver({
        root: intersectionObserverRoot !== undefined ? intersectionObserverRoot : listRef.current,
        threshold: 0.8,
        enabled: allowToMarkAsRead && list?.some((item: ICommentModel): boolean => {
            const mark = item.read_mark ?? item.read_marks?.find(
                    mark => mark.user_id === currentUserId
            );

            return !mark?.is_read;
        }) === true,
    });

    return <div
            {...wrapperProps}
            className={classnames('comment-list', {
                'comment-list--with-max-height': maxHeight,
            }, wrapperClassName)}
    >
        <RenderIf expression={position === 'top'}>
            <div className={classnames([
                'comment-list__message-form',
                'comment-list__message-form--top',
            ], {
                'comment-list__message-form--sticky': maxHeight,
            })}>{leaveMessageForm}</div>
        </RenderIf>

        {(isLoading && !list?.length) ? (
                <div className="d-flex justify-content-center">
                    <div className="comment-list__no-comments d-flex align-items-center">
                        <LoadingIndicator type="border" size="sm"/>
                        <Translate message="@common-phrases.loading" className="ms-2"/>
                    </div>
                </div>
        ) : (
                list?.length ? (
                        <div className="comment-list__items-wrapper" style={{maxHeight}} ref={listRef}>
                            {list?.map(comment => {
                                return <Item
                                        key={`comment-${comment.id}`}
                                        model={comment}
                                        allowToReply={allowToReply}
                                        onReply={comment => void setAnswerToMessage(comment)}
                                        onDeleted={onCommentDeleted ? () => onCommentDeleted(comment) : undefined}
                                        size={size}
                                        intersectionObserver={observer}
                                />;
                            })}
                        </div>
                ) : (
                        <Translate message="CommentsList.no-comments" className="comment-list__no-comments" tag="div"/>
                )
        )}

        <RenderIf expression={position === 'bottom'}>
            <div className={classnames([
                'comment-list__message-form',
                'comment-list__message-form--bottom',
            ], {
                'comment-list__message-form--sticky': maxHeight,
            })}>{leaveMessageForm}</div>
        </RenderIf>
    </div>;
}
